import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { Block, BlockHead, BlockHeadContent, BlockTitle, BlockDes, BlockBetween, Icon } from "../../components/Component";
import { handleDeleteApis, handleGetApis, handlePostApis } from "../../service/Api";
import RichTextEditor from "../../components/content/RichTextEditor";
import SeoFields from "../../components/content/SeoFields";
import ImageField from "../../components/content/ImageField";

const STOREFRONT = (process.env.REACT_APP_STOREFRONT_URL || "https://ryzhm.com").replace(/\/$/, "");

const toForm = (p) => ({
  title: p.title || "",
  subtitle: p.subtitle || "",
  body: p.body || "",
  meta_title: p.meta_title || "",
  meta_description: p.meta_description || "",
  keywords: p.keywords || "",
  og_image: p.og_image || "",
  hero_image: p.hero_image || "",
  hero_image_mobile: p.hero_image_mobile || "",
});

const PageEditor = () => {
  const { key } = useParams();
  const [page, setPage] = useState(null); // server copy (labels, flags)
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0); // remounts editor content after reset

  useEffect(() => {
    let cancelled = false;
    handleGetApis(`admin/pages/${key}`).then((res) => {
      if (cancelled) return;
      if (res?.data?.success) {
        setPage(res.data.data);
        setForm(toForm(res.data.data));
      } else {
        setLoadError(res?.data?.message || "Couldn't load this page.");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const applyServerCopy = (data) => {
    setPage(data);
    setForm(toForm(data));
    setEditorKey((k) => k + 1);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const res = await handlePostApis(`admin/pages/${key}`, form);
    setSaving(false);
    if (res?.data?.success) {
      setPage(res.data.data);
      toast.success("Saved — the live page updates within a few seconds.");
    } else {
      toast.error(res?.data?.message || "Couldn't save.");
    }
  };

  const handleReset = async () => {
    setResetOpen(false);
    const res = await handleDeleteApis(`admin/pages/${key}`);
    if (res?.data?.success) {
      applyServerCopy(res.data.data);
      toast.success("Back to the default text.");
    } else {
      toast.error(res?.data?.message || res?.message || "Couldn't reset.");
    }
  };

  if (loadError) {
    return (
      <Content>
        <p className="text-danger">{loadError}</p>
        <Link to="/pages">← Back to pages</Link>
      </Content>
    );
  }
  if (!form) {
    return (
      <Content>
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      </Content>
    );
  }

  return (
    <React.Fragment>
      <Head title={`Edit ${page.label}`} />
      <ToastContainer position="top-right" autoClose={3000} />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <Link to="/pages" className="text-soft fs-13px">
                ← Pages
              </Link>
              <BlockTitle page>{page.label}</BlockTitle>
              <BlockDes className="text-soft">
                <a href={`${STOREFRONT}${page.path}`} target="_blank" rel="noopener noreferrer">
                  {page.path} <Icon name="external" />
                </a>
                {page.is_default ? " • showing default text" : ""}
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <form onSubmit={handleSave}>
          <Row className="g-4">
            <Col lg={8}>
              <Block>
                <div className="card card-bordered">
                  <div className="card-inner">
                    {page.note && <div className="alert alert-light fs-13px">{page.note}</div>}
                    <div className="form-group">
                      <label className="form-label">Page heading (H1)</label>
                      <input type="text" className="form-control" maxLength={200} value={form.title} onChange={set("title")} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Small line above / below the heading</label>
                      <input type="text" className="form-control" maxLength={300} value={form.subtitle} onChange={set("subtitle")} />
                    </div>
                    {page.has_hero_image && (
                      <Row className="g-3">
                        <Col md={7}>
                          <ImageField
                            label="Hero image — desktop"
                            value={form.hero_image}
                            onChange={(hero_image) => setForm((f) => ({ ...f, hero_image }))}
                            hint="Landscape, at least 1920px wide. Empty = the built-in image."
                          />
                        </Col>
                        <Col md={5}>
                          <ImageField
                            label="Hero image — mobile (optional)"
                            value={form.hero_image_mobile}
                            onChange={(hero_image_mobile) => setForm((f) => ({ ...f, hero_image_mobile }))}
                            hint="Portrait, about 1080 × 1350 px. Empty = the desktop image is cropped to fit."
                          />
                        </Col>
                      </Row>
                    )}
                    {page.has_body && (
                      <div className="form-group">
                        <label className="form-label">Content</label>
                        <RichTextEditor
                          value={form.body}
                          resetKey={editorKey}
                          onChange={(body) => setForm((f) => ({ ...f, body }))}
                        />
                        <div className="form-note mt-1">Use “Heading” for section titles — it helps Google understand the page.</div>
                      </div>
                    )}
                  </div>
                </div>
              </Block>
            </Col>

            <Col lg={4}>
              <Block>
                <div className="card card-bordered">
                  <div className="card-inner">
                    <h6 className="title mb-3">Google & sharing</h6>
                    <SeoFields
                      value={form}
                      onChange={(seo) => setForm((f) => ({ ...f, ...seo }))}
                      path={page.path}
                      fallbackTitle={form.title}
                      appendsBrand={key !== "perfumes"}
                    />
                  </div>
                </div>
              </Block>

              <div className="d-flex gap-2 mt-3">
                <Button color="primary" type="submit" disabled={saving}>
                  {saving ? <Spinner size="sm" /> : "Save"}
                </Button>
                {!page.is_default && (
                  <Button color="light" type="button" onClick={() => setResetOpen(true)}>
                    Reset to default
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </form>

        <Modal isOpen={resetOpen} toggle={() => setResetOpen(false)} centered>
          <ModalHeader toggle={() => setResetOpen(false)}>Reset this page?</ModalHeader>
          <ModalBody>Your saved text and SEO for this page will be removed and the built-in default text shown again.</ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setResetOpen(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleReset}>
              Reset
            </Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default PageEditor;
